import React, { useState, useMemo, useContext, useEffect, Suspense } from 'react'
import type {
  LineChart as LineChartType,
  Line as LineType,
  XAxis as XAxisType,
  YAxis as YAxisType,
  CartesianGrid as CartesianGridType,
  Tooltip as TooltipType,
  Legend as LegendType,
  ResponsiveContainer as ResponsiveContainerType,
} from 'recharts'
import { CareerTooltip } from '../CareerTooltip'
import { PlayerProfileContext } from '../../../../../../Context'
import { StatDefinition } from '../careerProgressionChart.types'
import {
  ChartContainer,
} from '../styles'

type RechartsModule = {
  LineChart: typeof LineChartType
  Line: typeof LineType
  XAxis: typeof XAxisType
  YAxis: typeof YAxisType
  CartesianGrid: typeof CartesianGridType
  Tooltip: typeof TooltipType
  Legend: typeof LegendType
  ResponsiveContainer: typeof ResponsiveContainerType
}

type CareerProgressionChartProps = {
  statsForToggle: StatDefinition[]
  selectedStats: string[]
}

export const CareerProgressionChart: React.FC<CareerProgressionChartProps> = ({
  statsForToggle,
  selectedStats,
}) => {
  const [rechartsModule, setRechartsModule] = useState<RechartsModule | null>(null)
  const { player } = useContext(PlayerProfileContext)!

  useEffect(() => {
    import('recharts').then((module) => {
      setRechartsModule({
        LineChart: module.LineChart,
        Line: module.Line,
        XAxis: module.XAxis,
        YAxis: module.YAxis,
        CartesianGrid: module.CartesianGrid,
        Tooltip: module.Tooltip,
        Legend: module.Legend,
        ResponsiveContainer: module.ResponsiveContainer,
      })
    })
  }, [player])

  const sortedCareerData = useMemo(() => {
    const allSeasons = player?.allSeasonStats
    if (!allSeasons || allSeasons.length < 1) return []
    const mappedStats = allSeasons.map((season) => ({
      team: season.teamId,
      ...season,
    }))
    return mappedStats.sort((a, b) => a.year - b.year)
  }, [player])

  if (!sortedCareerData) return null

  const renderChart = () => {
    if (!rechartsModule) return null

    const {
      LineChart,
      Line,
      XAxis,
      YAxis,
      CartesianGrid,
      Tooltip,
      Legend,
      ResponsiveContainer,
    } = rechartsModule

    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={sortedCareerData}
          margin={{
            top: 20,
            right: 30,
            left: 5,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e0e0" />
          <XAxis
            dataKey="year"
            tick={{ fill: '#666', fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            padding={{ left: 30, right: 30 }}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fill: '#666', fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            width={40}
          />
          <Tooltip
            content={
              <CareerTooltip showPerGameStats={false} availableStats={statsForToggle} />
            }
            cursor={{ stroke: '#ccc', strokeWidth: 1, strokeDasharray: '3 3' }}
          />
          <Legend
            verticalAlign="top"
            height={36}
            iconType="circle"
            iconSize={10}
            wrapperStyle={{ paddingTop: 10, paddingBottom: 10 }}
          />

          {selectedStats.map((statId) => {
            const stat = statsForToggle.find((s) => s.id === statId)
            if (!stat) return null
            const dataKey = stat.key
            return (
              <Line
                key={statId}
                type="monotone"
                dataKey={dataKey}
                name={stat.name}
                stroke={stat.color}
                strokeWidth={2}
                activeDot={{ r: 6, strokeWidth: 0, fill: stat.color }}
                dot={{ r: 3, strokeWidth: 0, fill: stat.color }}
                connectNulls
              />
            )
          })}
        </LineChart>
      </ResponsiveContainer>
    )
  }

  return (
        <ChartContainer>
          <Suspense fallback={<></>}>{renderChart()}</Suspense>
        </ChartContainer>
  )
}
